import { useQueryClient } from '@tanstack/react-query';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDataBoxEvents } from '@gov-nx/core/events';
import { useMySubmissionConnection } from '@gov-nx/core/hooks';
import { useLanguageNamespaceLoader } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { PageCode } from '@gov-nx/module/page';
import { useDataBoxStoreLists } from '@gov-nx/store';
import { useBoolean } from '@gov-nx/utils/common';
import { useDataBoxUnreadMessages } from '../hooks/useDataBoxUnreadMessagesCounts';
import { useMessageDetail } from '../hooks/useMessageDetail';
import { useMessageOperations } from '../hooks/useMessageOperations';
import type {
	DataBoxConfirmModalType,
	DataBoxFolderType,
} from './DataBoxes.types';
import type {
	DataBoxMessageDetailContextType,
	DataBoxMessageDetailLoadingItemType,
} from './MessageDetailContext.types';

export const DataBoxMessageDetailContext =
	createContext<Nullable<DataBoxMessageDetailContextType>>(null);

interface DataBoxMessageDetailProviderProps {
	dataBoxId: string;
	folderType: DataBoxFolderType;
	messageId: number;
	children: React.ReactNode;
}

export function DataBoxMessageDetailProvider({
	dataBoxId,
	folderType,
	messageId,
	children,
}: DataBoxMessageDetailProviderProps) {
	const queryClient = useQueryClient();
	const mySubmissionLanguage = useLanguageNamespaceLoader(
		PageCode['moje-podani']
	);

	const {
		message,
		isMessageLoading,
		refetchMessage,
		recipientDataBox,
		senderDataBox,
		currentDataBox,
	} = useMessageDetail({
		dataBoxId,
		folderType,
		messageId,
	});

	const { messageRead, hideMessageList, resetActionInfo } = useDataBoxEvents();

	const [loadingItems, setLoadingItems] = useState<
		Partial<Record<DataBoxMessageDetailLoadingItemType, boolean>>
	>({});

	const updateLoadingItems = (
		loadingItem: DataBoxMessageDetailLoadingItemType,
		isLoading: boolean
	) => {
		if (isLoading) {
			setLoadingItems((loadingItems) => ({
				...loadingItems,
				[loadingItem]: true,
			}));
		} else {
			setLoadingItems((loadingItems) => ({
				...loadingItems,
				[loadingItem]: false,
			}));
		}
	};

	const { refetchDataBoxUnreadMessages } = useDataBoxUnreadMessages();

	useEffect(() => {
		setTimeout(() => {
			hideMessageList({ hidden: true });
		});

		return () => {
			setTimeout(() => {
				hideMessageList({ hidden: false });
				resetActionInfo();
			});
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, []);

	useEffect(() => {
		setLoadingItems({});

		if (message) {
			queryClient.invalidateQueries({
				queryKey: ['data-box-unread-messages-list'],
			});
			messageRead({ messageId });
			refetchDataBoxUnreadMessages();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [message]);

	useDataBoxEvents({
		onMessagesArchive: () => refetchMessage(),
	});

	const [showAllAttachments, setShowAllAttachments] = useState(false);
	const [confirmModal, setConfirmModal] =
		useState<Nullable<DataBoxConfirmModalType>>(null);
	const vaultConfirmModal = useBoolean(false);

	const messageOperations = useMessageOperations({
		message,
		dataBoxId,
		folderType,
		messageId,
		updateLoadingItems,
		setConfirmModal,
	});

	const { connectedDataBoxes } = useDataBoxStoreLists();

	const disableNewMessageButton = !connectedDataBoxes.some(
		(dataBox) => dataBox.datovaSchrankaId === dataBoxId
	);

	const mySubmissionConnection = useMySubmissionConnection({ messageId });

	return (
		<DataBoxMessageDetailContext.Provider
			value={{
				dataBoxId,
				dataBox: currentDataBox,
				recipientDataBox,
				senderDataBox,
				folderType,
				messageId,
				message,
				isLoading:
					isMessageLoading ||
					messageOperations.isLoading ||
					mySubmissionLanguage.isLoading,
				loadingItems,
				messageOperations,
				confirmModal,
				vaultConfirmModal,
				setConfirmModal,
				showAllAttachments,
				setShowAllAttachments,
				disableNewMessageButton,
				mySubmissionConnection,
			}}>
			{children}
		</DataBoxMessageDetailContext.Provider>
	);
}

export const useDataBoxMessageDetailContext =
	(): DataBoxMessageDetailContextType =>
		useContext(DataBoxMessageDetailContext) as DataBoxMessageDetailContextType;
